/**
 * The review module's server state (APP_SPEC box 6): the project's
 * pending attestations, its findings, and the one write -- a person's
 * answer. PENDING IS DERIVED on the server (matching rule x subject,
 * minus answered), so an answer never edits a list here: it invalidates
 * the pending key and the refetch is what makes the question leave.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { typedClient, throwOnServerError, type components } from '../../api';

export type PendingAttestation = components['schemas']['PendingAttestation'];
export type AttestationAnswer = components['schemas']['AttestationAnswer'];
export type AttestationVerdict = components['schemas']['AttestationVerdict'];
export type StandingAttestation = components['schemas']['StandingAttestation'];
export type FindingView = components['schemas']['FindingView'];

/** The answer as the screen states it: the project is the hook's. */
export type Answer = Omit<AttestationAnswer, 'project_id'>;

export const pendingKey = (projectId: string) => ['attestations', 'pending', projectId] as const;

/** The questions the project still owes a person, each with the citation it stands on. */
export function usePending(projectId: string) {
  return useQuery<PendingAttestation[]>({
    queryKey: pendingKey(projectId),
    queryFn: async () => {
      const response = await typedClient.GET('/v1/attestations/pending', {
        params: { query: { project_id: projectId } },
      });
      throwOnServerError(response);
      return response.data ?? [];
    },
  });
}

/** The latest inspection's findings, each citing its book from the inspection's own snapshot. */
export function useFindings(projectId: string) {
  return useQuery<FindingView[]>({
    queryKey: ['findings', projectId],
    queryFn: async () => {
      const response = await typedClient.GET('/v1/findings', {
        params: { query: { project_id: projectId } },
      });
      throwOnServerError(response);
      return response.data ?? [];
    },
  });
}

/** Record one answer; the pending key is invalidated on success, since pending is derived, never stored. */
export function useAnswer(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<StandingAttestation, Error, Answer>({
    mutationFn: async (answer) => {
      const response = await typedClient.POST('/v1/attestations', {
        body: { project_id: projectId, ...answer },
      });
      throwOnServerError(response);
      if (!response.data) throw new Error('the answer came back without a standing record');
      return response.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: pendingKey(projectId) }),
  });
}
